import { ApolloClient, InMemoryCache, from, split } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { getMainDefinition } from '@apollo/client/utilities';
import { WebSocketLink } from '@apollo/client/link/ws';
import { SubscriptionClient } from 'subscriptions-transport-ws';
import { createUploadLink } from 'apollo-upload-client';

// WebSocket 連接
const wsClient = new SubscriptionClient(
  process.env.REACT_APP_WS_URI || 'ws://localhost:4000/graphql',
  {
    reconnect: true,
    connectionParams: () => {
      const token = localStorage.getItem('token');
      console.log('🔐 WebSocket 認證 token:', token ? '存在' : '不存在');
      return {
        authorization: token ? `Bearer ${token}` : '',
      };
    },
    onConnect: () => {
      console.log('🔌 WebSocket 連接成功');
    },
    onDisconnect: () => {
      console.log('🔌 WebSocket 連接斷開');
    },
    onError: (error) => {
      console.error('❌ WebSocket 錯誤:', error);
    }
  }
);

const wsLink = new WebSocketLink(wsClient);

// HTTP 連接（支援文件上傳）
const uploadLink = createUploadLink({
  uri: process.env.REACT_APP_GRAPHQL_URI || 'http://localhost:4000/graphql',
  credentials: 'include'
});

// 錯誤處理
const errorLink = onError(({ graphQLErrors, networkError }) => {
  if (graphQLErrors) {
    graphQLErrors.forEach(({ message, locations, path }) => {
      console.error(
        `[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`
      );
    });
  }

  if (networkError) {
    console.error(`[Network error]: ${networkError}`);
  }
});

// 認證連結
const authLink = setContext((_, { headers }) => {
  const token = localStorage.getItem('token');
  
  return {
    headers: {
      ...headers,
      authorization: token ? `Bearer ${token}` : "",
    }
  };
});

// 根據操作類型選擇連結
const splitLink = split(
  ({ query }) => {
    const definition = getMainDefinition(query);
    return (
      definition.kind === 'OperationDefinition' &&
      definition.operation === 'subscription'
    );
  },
  wsLink, // 使用 WebSocket 處理 subscriptions
  from([errorLink, authLink, uploadLink]) // 使用 HTTP 處理 queries 和 mutations（支援文件上傳）
);

// 創建 Apollo Client
const client = new ApolloClient({
  link: splitLink,
  cache: new InMemoryCache({
    typePolicies: {
      Query: {
        fields: {
          myQuestionSets: {
            merge: false, // 不自動合併，每次都重新獲取
          },
          myTests: {
            merge: false, // 不自動合併，每次都重新獲取
          },
          myTestAttempts: {
            merge: false, // 不自動合併，每次都重新獲取
          },
          chatroomMessages: {
            merge: false, // 不自動合併，每次都重新獲取
          }
        }
      }
    }
  }),
  defaultOptions: {
    watchQuery: {
      errorPolicy: 'all',
      fetchPolicy: 'cache-and-network', // 優先使用快取，但同時從網路獲取最新資料
    },
    query: {
      errorPolicy: 'all',
      fetchPolicy: 'cache-and-network',
    },
  },
});

// 監聽認證狀態變化，重新連接 WebSocket
const updateWebSocketConnection = () => {
  const token = localStorage.getItem('token');
  if (token) {
    wsClient.close(false, false);
    wsClient.connect();
  }
};

// 導出 client 和工具函數
export { updateWebSocketConnection };
export default client; 